// Third-party packages
import {
	CollectionJson,
	DocumentTemplatesProps,
} from '@@types/Collection';
import {
	ContactJson,
} from '@@types/Contact';
import {
	ObservatoryReport,
	UserJson,
} from '@@types/User';
import {
	APP_CONF_VARS,
} from '@appConf/vars.conf';
import {
	createSlice,
} from '@reduxjs/toolkit';

export interface GlobalUser extends UserJson {
	auth_step?: string;
	authenticated: boolean;
	expired_session: number | undefined;
	observatory_reports: ObservatoryReport[];
	otherUsers: UserJson[];
	company: {
		brokers: CollectionJson<ContactJson>;
		document_templates: CollectionJson<DocumentTemplatesProps>;
		id: number;
		name: string;
	};
	token: string;
}

// Define the initial state using that type (flattened, no instance)
const initialState: GlobalUser = {
	authenticated: false,
	company: {
		brokers: {
			collection: [],
			count: 0,
		},
		document_templates: {
			collection: [],
			count: 0,
		},
		id: 0,
		name: '',
	},
	expired_session: undefined,
	modules: {},
	observatory_reports: [],
	otherUsers: [],
	token: '',
};

export const userSlice = createSlice({
	initialState,
	name: 'user',
	reducers: {
		userClearOtherUsers: (state) => {
			state.otherUsers = [];
		},
		userLogin: (state, action) => {
			Object.assign(state, action.payload);
			state.authenticated = true;
		},
		userLogout: (state) => {
			state.authenticated = false;
		},
		userNeedsAuth: (state, action) => {
			state.auth_step = action.payload;
		},
		userNotifyOtherUser: (state, action) => {
			// On the user workflow with multiple connection
			// To store which other user has been notified

			// Get targeted other user data from actual state
			const targetedOtherUser = state.otherUsers?.filter(otherUser => otherUser.id === action.payload.id)[0];

			// Get other users array without targeted one
			const filteredOtherUsers = state.otherUsers?.filter(otherUser => otherUser.id !== action.payload.id) ?? [];

			// Create new targeted user data state
			const newTargetedOtherUsers = {
				...targetedOtherUser,
				notified: true,
			};

			// Enhance targeted user data
			filteredOtherUsers.push(newTargetedOtherUsers);

			// Update slice state
			state.otherUsers = filteredOtherUsers;
		},
		userSetCompanyBrokers: (state, action) => {
			state.company = {
				...state.company,
				brokers: action.payload.brokers,
			};
		},
		userSetDocumentsTemplates: (state, action) => {
			state.company = {
				...state.company,
				document_templates: action.payload.document_templates,
			};
		},
		userSetOtherUsers: (state, action) => {
			state.otherUsers = action.payload;
		},
		userUpdate: (state, action) => {
			Object.assign(state, action.payload);
		},
		userUpdateModuleActivity: (state, action) => {
			state.modules = {
				...state.modules,
				[action.payload.module]: {
					...state.modules?.[action.payload.module],
					activity: {
						...state.modules?.[action.payload.module]?.activity,
						[action.payload.activity]: action.payload.value,
					},
				},
			};
		},
		userUpdateOservatoryReports: (state, action) => {
			state.observatory_reports = action.payload;
		},
		userUpdateSession: (state) => {
			const newExpiredDate = new Date(Date.now() + APP_CONF_VARS.session.lifetime).getTime();
			state.expired_session = newExpiredDate;
		},
	},
});

// Action creators are generated for each case reducer function
export const {
	userClearOtherUsers,
	userLogin,
	userLogout,
	userNeedsAuth,
	userNotifyOtherUser,
	userSetCompanyBrokers,
	userSetDocumentsTemplates,
	userSetOtherUsers,
	userUpdate,
	userUpdateModuleActivity,
	userUpdateOservatoryReports,
	userUpdateSession,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
